import React from 'react';
import {
  Grid,
  GridRow,
  ShareBenchIcon,
  GridColumn,
  Button,
  CardSection,
  Card,
  LargeHeader,
  SmallHeader,
  Paragraph,
  CardFooter,
  Gutter,
  PluralizeUtil,
} from '@axiom/ui';

import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';
import { useTalentSearchCandidateStatsData } from '../../hooks/useTalentSearchCandidateStatsData';

export interface TalentSearchEmptyProps {
  activeFilters: TalentSearchActiveQueryFilters;
  handleClearFiltersClick: () => Promise<void> | void;
  handleReachOutClick: () => void;
  isHomeTabSelected?: boolean;
  handleAddFavoriteClick: (view: string) => void;
}

interface DataStrings {
  title: string;
  subtitle: string;
  body: string | React.JSX.Element;
}

export const TalentSearchEmpty = ({
  handleReachOutClick,
  handleClearFiltersClick,
  isHomeTabSelected = false,
  handleAddFavoriteClick,
  activeFilters,
}: TalentSearchEmptyProps) => {
  const stats = useTalentSearchCandidateStatsData({ activeFilters });
  const getStrings = (isHome: boolean, count: number): DataStrings => {
    const pluralizedProfile = PluralizeUtil({ text: 'profile', count });
    const subtitleDefault = isHome
      ? "They can be hard to find but we've got 'em. Try adjusting the filters to broaden your search."
      : '';
    const isHomeTitleDefault =
      count > 0
        ? 'No Profiles? No Problem.'
        : 'Looking for legal talent with unique or high-demand qualifications?';

    return {
      title: isHome
        ? isHomeTitleDefault
        : "Looks like you don't have any saved talent",
      subtitle: isHome && count > 0 ? '' : subtitleDefault,
      body: isHome ? (
        count > 0 ? (
          <>
            We have <span data-test="OPT_OUT_COUNT">{count}</span>{' '}
            {pluralizedProfile} for Axiom talent who are available and match
            your filters but aren’t shown here yet. Please reach out if you’d
            like to find out more about them.
          </>
        ) : (
          "If that doesn't work, please reach out to Axiom and let us help. We can peek at profiles that aren't shown here yet and find your one-of-a-kind legal talent before anyone else does."
        )
      ) : (
        "Saved talent profiles that you're interested in so you can easily find them later."
      ),
    };
  };
  const { title, subtitle, body } = getStrings(
    isHomeTabSelected,
    stats.endOfTalentCount ?? 0
  );

  return (
    <Card name="TALENT_SEARCH_EMPTY">
      <CardSection divider>
        <Grid>
          <GridRow>
            <GridColumn largeScreenWidth={2} tabletWidth={3} mobileWidth={12}>
              <ShareBenchIcon width={110} height={110} />
            </GridColumn>
            <GridColumn largeScreenWidth={10} tabletWidth={9} mobileWidth={12}>
              <Gutter bottom="16px">
                <LargeHeader name="TALENT_SEARCH_EMPTY_HEADER">
                  {title}
                </LargeHeader>
              </Gutter>
              <Gutter bottom="16px">
                <SmallHeader name="TALENT_SEARCH_EMPTY_SUBHEADER">
                  {subtitle}
                </SmallHeader>
              </Gutter>
              <Paragraph name="TALENT_SEARCH_EMPTY_BODY">{body}</Paragraph>
            </GridColumn>
          </GridRow>
        </Grid>
      </CardSection>
      <CardFooter>
        {!!isHomeTabSelected && (
          <>
            <Button
              name="TALENT_SEARCH_EMPTY_CLEAR_FILTERS"
              variation="outline"
              onClick={async () => {
                await handleClearFiltersClick();
              }}
            >
              Clear Filters
            </Button>

            <Button
              name="TALENT_SEARCH_EMPTY_REACH_OUT"
              onClick={() => {
                handleReachOutClick();
              }}
            >
              Reach Out
            </Button>
          </>
        )}
        {!isHomeTabSelected && (
          <Button
            name="TALENT_SEARCH_ADD_FAVORITES"
            onClick={() => {
              handleAddFavoriteClick('home');
            }}
          >
            Add Saved Talent
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
